import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, DollarSign, User, MapPin, Package, ChevronDown, ChevronUp, Truck, Star } from 'lucide-react';
import SignaturePad from './SignaturePad';
import CustomerSearch from './CustomerSearch';

const QUICK_PRODUCTS = [
  "Caja de Comida", "Caja Ropa Usada", "Caja Normal", "Caja Closet",
  "Nevera Grande", "Nevera Normal", "Lavadora", "Estufa", "Secadora",
  "Aceite Amazing Grande", "Aceite Mazola Grande", "Aceite Mazola Pequeno",
  "Caja con Mona", "Tanque Grande", "Tanque Normal",
  "Arroz Carolina 20 Lbs", "Arroz Ricelang 50 Lbs", "Arroz Canilla 50 Lbs",
  "Aceite Wesson Pequeno"
];

const ShipmentForm = ({ editingShipment, remitentes, destinatarios, openFurgones, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    remitente_id: '', destinatario_id: '', productos: [],
    peso: 0, dimensiones: '', numero_furgon: '',
    subtotal: 0, descuento: 0, impuestos: 0, costo_envio: 0, total: 0,
    metodo_pago: 'efectivo', tipo_factura: 'contado',
    monto_pagado: 0, monto_pendiente: 0, notas: ''
  });

  const [firmaRemitente, setFirmaRemitente] = useState(null);
  const [firmaRecolector, setFirmaRecolector] = useState(null);
  const [showQuickProducts, setShowQuickProducts] = useState(true);
  const [showSignatures, setShowSignatures] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [frecuentProducts, setFrecuentProducts] = useState([]);

  useEffect(() => {
    if (editingShipment) {
      setFormData({
        remitente_id: editingShipment.remitente_id || '',
        destinatario_id: editingShipment.destinatario_id || '',
        productos: (editingShipment.productos || []).map(p => ({
          descripcion: p.descripcion || '',
          cantidad: p.cantidad || 1,
          precio_unitario: p.precio_unitario || 0,
          subtotal: p.subtotal || 0
        })),
        peso: editingShipment.peso || 0,
        dimensiones: editingShipment.dimensiones || '',
        numero_furgon: editingShipment.numero_furgon || '',
        subtotal: editingShipment.subtotal || 0,
        descuento: editingShipment.descuento || 0,
        impuestos: editingShipment.impuestos || 0,
        costo_envio: editingShipment.costo_envio || 0,
        total: editingShipment.total || 0,
        metodo_pago: editingShipment.metodo_pago || 'efectivo',
        tipo_factura: editingShipment.tipo_factura || 'contado',
        monto_pagado: editingShipment.monto_pagado || 0,
        monto_pendiente: editingShipment.monto_pendiente || 0,
        notas: editingShipment.notas || ''
      });
      if (editingShipment.remitente_id) fetchFrequentProducts(editingShipment.remitente_id);
    } else {
      const defaultFurgon = openFurgones.length > 0 ? openFurgones[0].numero : '';
      setFormData(prev => ({ ...prev, numero_furgon: defaultFurgon }));
    }
  }, [editingShipment, openFurgones]);

  const fetchFrequentProducts = async (remitenteId) => {
    try {
      const res = await api.get(`/shipments/remitente/${remitenteId}/productos-frecuentes`);
      setFrecuentProducts(res.data || []);
    } catch {
      setFrecuentProducts([]);
    }
  };

  const handleRemitenteChange = (id) => {
    setFormData(prev => ({ ...prev, remitente_id: id }));
    if (id) {
      fetchFrequentProducts(id);
    } else {
      setFrecuentProducts([]);
    }
  };

  const addProductoFrecuente = (fp) => {
    const nuevo = {
      descripcion: fp.descripcion,
      cantidad: fp.ultima_cantidad || 1,
      precio_unitario: fp.ultimo_precio || 0,
      subtotal: (fp.ultima_cantidad || 1) * (fp.ultimo_precio || 0)
    };
    const updated = [...formData.productos, nuevo];
    recalcProducts(updated);
  };

  const addProductoRapido = (nombre) => {
    const fp = frecuentProducts.find(p => p.descripcion === nombre);
    if (fp) {
      addProductoFrecuente(fp);
    } else {
      const updated = [...formData.productos, { descripcion: nombre, cantidad: 1, precio_unitario: 0, subtotal: 0 }];
      recalcProducts(updated);
    }
  };

  const addProductoManual = () => {
    const updated = [...formData.productos, { descripcion: '', cantidad: 1, precio_unitario: 0, subtotal: 0 }];
    setFormData(prev => ({ ...prev, productos: updated }));
  };

  const updateProducto = (index, field, value) => {
    const updated = [...formData.productos];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'cantidad' || field === 'precio_unitario') {
      const cant = field === 'cantidad' ? value : updated[index].cantidad;
      const precio = field === 'precio_unitario' ? value : updated[index].precio_unitario;
      updated[index].subtotal = (cant || 0) * (precio || 0);
    }
    recalcProducts(updated);
  };

  const removeProducto = (index) => {
    const updated = formData.productos.filter((_, i) => i !== index);
    recalcProducts(updated);
  };

  const recalcProducts = (productos) => {
    const subtotal = productos.reduce((sum, p) => sum + (p.subtotal || 0), 0);
    const total = subtotal - (formData.descuento || 0);
    const monto_pagado = formData.tipo_factura === 'contado' ? total : formData.monto_pagado;
    const monto_pendiente = formData.tipo_factura === 'credito' ? total - monto_pagado : 0;
    setFormData(prev => ({ ...prev, productos, subtotal, total, monto_pagado, monto_pendiente }));
  };

  const recalcFinancials = (updates) => {
    const newData = { ...formData, ...updates };
    const total = newData.subtotal - (newData.descuento || 0);
    // When switching TO credito, reset monto_pagado to 0
    let monto_pagado;
    if (updates.tipo_factura === 'credito' && formData.tipo_factura !== 'credito') {
      monto_pagado = 0;
    } else if (newData.tipo_factura === 'contado') {
      monto_pagado = total;
    } else {
      monto_pagado = newData.monto_pagado;
    }
    const monto_pendiente = newData.tipo_factura === 'credito' ? total - monto_pagado : 0;
    setFormData(prev => ({ ...prev, ...updates, total, monto_pagado, monto_pendiente }));
  };

  const sanitizeFormData = () => ({
    ...formData,
    peso: Number(formData.peso) || 0,
    subtotal: Number(formData.subtotal) || 0,
    descuento: Number(formData.descuento) || 0,
    impuestos: Number(formData.impuestos) || 0,
    costo_envio: Number(formData.costo_envio) || 0,
    total: Number(formData.total) || 0,
    monto_pagado: Number(formData.monto_pagado) || 0,
    monto_pendiente: Number(formData.monto_pendiente) || 0,
    productos: formData.productos.map(p => ({
      descripcion: p.descripcion || '',
      cantidad: Number(p.cantidad) || 1,
      precio_unitario: Number(p.precio_unitario) || 0,
      subtotal: Number(p.subtotal) || 0
    }))
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.remitente_id || !formData.destinatario_id) {
      toast.error('Selecciona remitente y destinatario');
      return;
    }
    if (formData.productos.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }
    const hasEmptyProduct = formData.productos.some(p => !p.descripcion);
    if (hasEmptyProduct) {
      toast.error('Todos los productos deben tener descripcion');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(sanitizeFormData(), firmaRemitente, firmaRecolector);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(`Error: ${detail.map(d => d.msg || JSON.stringify(d)).join(', ')}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Error al guardar envio');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Build ordered quick products: frequent first, then the rest
  const frequentNames = new Set(frecuentProducts.map(fp => fp.descripcion));
  const remainingQuickProducts = QUICK_PRODUCTS.filter(p => !frequentNames.has(p));

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="shipment-form">
      {/* SECTION 1: Remitente & Destinatario with Search */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-base font-bold flex items-center gap-2 text-blue-700">
            <User className="h-4 w-4" /> Remitente *
          </Label>
          <CustomerSearch
            customers={remitentes}
            value={formData.remitente_id}
            onChange={handleRemitenteChange}
            placeholder="Buscar por nombre, telefono, cedula, direccion..."
            colorScheme="blue"
            testId="select-remitente"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-base font-bold flex items-center gap-2 text-green-700">
            <MapPin className="h-4 w-4" /> Destinatario *
          </Label>
          <CustomerSearch
            customers={destinatarios}
            value={formData.destinatario_id}
            onChange={(id) => setFormData(prev => ({ ...prev, destinatario_id: id }))}
            placeholder="Buscar por nombre, telefono, cedula, direccion..."
            colorScheme="green"
            testId="select-destinatario"
          />
        </div>
      </div>

      {/* SECTION 2: Products */}
      <div className="border-2 border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b">
          <h3 className="font-bold text-base flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" /> Productos
            {formData.productos.length > 0 && (
              <Badge className="bg-blue-600 text-white ml-2">{formData.productos.length}</Badge>
            )}
          </h3>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowQuickProducts(!showQuickProducts)} className="text-blue-600">
            {showQuickProducts ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
            Seleccion Rapida
          </Button>
        </div>

        {showQuickProducts && (
          <div className="p-3 bg-blue-50/50 border-b space-y-3">
            {/* Frequent products chips */}
            {frecuentProducts.length > 0 && (
              <div>
                <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Star className="h-3 w-3" /> Frecuentes
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {frecuentProducts.map((fp) => (
                    <button
                      key={fp.descripcion}
                      type="button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-semibold rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors"
                      onClick={() => addProductoFrecuente(fp)}
                      data-testid={`frequent-product-${fp.descripcion.replace(/\s/g, '-').toLowerCase()}`}
                    >
                      <Star className="h-3 w-3 text-amber-500" />
                      {fp.descripcion}
                      <span className="text-xs text-amber-500 ml-1">${fp.ultimo_precio}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Dropdown for all products */}
            <div>
              <Select onValueChange={(v) => { addProductoRapido(v); }}>
                <SelectTrigger data-testid="quick-product-select" className="h-12 border-2 border-blue-200 bg-white text-base font-semibold">
                  <SelectValue placeholder="Seleccionar articulo..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {(frecuentProducts.length > 0 ? remainingQuickProducts : QUICK_PRODUCTS).map((prod) => (
                    <SelectItem key={prod} value={prod} data-testid={`quick-product-${prod.replace(/\s/g, '-').toLowerCase()}`}>
                      {prod}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Products List */}
        <div className="p-3 space-y-2">
          {formData.productos.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Package className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-base">Selecciona productos arriba o agrega manualmente</p>
            </div>
          ) : (
            formData.productos.map((producto, index) => (
              <div key={index} className="p-3 rounded-lg bg-white border-2 border-gray-100 hover:border-blue-200 transition-colors space-y-2" data-testid={`product-row-${index}`}>
                {/* Description - full width */}
                <div className="flex items-center gap-2">
                  <Input
                    value={producto.descripcion}
                    onChange={(e) => updateProducto(index, 'descripcion', e.target.value)}
                    placeholder="Nombre del producto"
                    className="h-10 text-base font-semibold border-2 border-gray-200 focus:border-blue-400 flex-1"
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeProducto(index)} className="h-10 w-10 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {/* Qty x Price = Subtotal */}
                <div className="flex items-center gap-2">
                  <div className="w-20 flex-shrink-0">
                    <Input
                      type="number" min="1"
                      value={producto.cantidad}
                      onChange={(e) => updateProducto(index, 'cantidad', parseInt(e.target.value) || 1)}
                      className="h-9 text-center text-sm font-bold border-2 rounded-lg"
                      title="Cantidad"
                    />
                  </div>
                  <span className="text-gray-400 font-bold text-sm">x</span>
                  <div className="flex-1">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">$</span>
                      <Input
                        type="number" step="0.01" min="0"
                        value={producto.precio_unitario || ''}
                        onChange={(e) => updateProducto(index, 'precio_unitario', parseFloat(e.target.value) || 0)}
                        className="h-9 text-sm font-bold pl-6 border-2 rounded-lg"
                        placeholder="0.00"
                        title="Precio"
                      />
                    </div>
                  </div>
                  <span className="text-gray-400 font-bold text-sm">=</span>
                  <div className="w-24 flex-shrink-0 text-right font-bold text-base text-green-700 px-1">
                    ${(producto.subtotal || 0).toFixed(2)}
                  </div>
                </div>
              </div>
            ))
          )}
          <Button type="button" variant="outline" onClick={addProductoManual} className="w-full h-10 border-dashed border-2 text-gray-500 hover:text-blue-600 hover:border-blue-300">
            <Plus className="mr-2 h-4 w-4" /> Agregar Producto Manual
          </Button>
        </div>
      </div>

      {/* SECTION 3: Payment + Furgon Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label className="text-sm font-bold text-gray-600">Tipo Factura</Label>
          <Select value={formData.tipo_factura} onValueChange={(v) => recalcFinancials({ tipo_factura: v })}>
            <SelectTrigger className="h-11 border-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="contado">Contado</SelectItem>
              <SelectItem value="credito">Credito</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-sm font-bold text-gray-600">Metodo Pago</Label>
          <Select value={formData.metodo_pago} onValueChange={(v) => setFormData(prev => ({ ...prev, metodo_pago: v }))}>
            <SelectTrigger className="h-11 border-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="efectivo">Efectivo</SelectItem>
              <SelectItem value="zelle">Zelle</SelectItem>
              <SelectItem value="credito">Credito</SelectItem>
              <SelectItem value="pago_rd">Pago en RD</SelectItem>
              <SelectItem value="tarjeta">Tarjeta</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-sm font-bold text-gray-600 flex items-center gap-1">
            <Truck className="h-3.5 w-3.5" /> Furgon
          </Label>
          <Select value={formData.numero_furgon || 'ninguno'} onValueChange={(v) => setFormData(prev => ({ ...prev, numero_furgon: v === 'ninguno' ? '' : v }))}>
            <SelectTrigger data-testid="furgon-select" className="h-11 border-2"><SelectValue placeholder="Sin furgon" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ninguno">Sin furgon</SelectItem>
              {openFurgones.map(f => (
                <SelectItem key={f.id} value={f.numero}>{f.numero} - {f.estado}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-sm font-bold text-gray-600">Descuento</Label>
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
            <Input
              type="number" step="0.01" min="0"
              value={formData.descuento || ''}
              onChange={(e) => recalcFinancials({ descuento: parseFloat(e.target.value) || 0 })}
              className="h-11 pl-7 border-2" placeholder="0.00"
            />
          </div>
        </div>
      </div>

      {/* Credit payment + Notes */}
      {formData.tipo_factura === 'credito' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-sm font-bold text-gray-600">Monto Pagado</Label>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
              <Input
                type="number" step="0.01" min="0"
                value={formData.monto_pagado || ''}
                onChange={(e) => recalcFinancials({ monto_pagado: parseFloat(e.target.value) || 0 })}
                className="h-11 pl-7 border-2" placeholder="0.00"
              />
            </div>
          </div>
        </div>
      )}

      {/* SECTION 4: Total Summary */}
      <div className="rounded-xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 via-white to-indigo-50 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Subtotal</p>
              <p className="text-lg font-bold">${(formData.subtotal || 0).toFixed(2)}</p>
            </div>
            {formData.descuento > 0 && (
              <div className="text-center">
                <p className="text-xs font-bold text-red-400 uppercase tracking-wide">Descuento</p>
                <p className="text-lg font-bold text-red-600">-${(formData.descuento || 0).toFixed(2)}</p>
              </div>
            )}
            {formData.tipo_factura === 'credito' && formData.monto_pendiente > 0 && (
              <div className="text-center">
                <p className="text-xs font-bold text-orange-400 uppercase tracking-wide">Pendiente</p>
                <p className="text-lg font-bold text-orange-600">${(formData.monto_pendiente || 0).toFixed(2)}</p>
              </div>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs font-bold text-blue-400 uppercase tracking-wide">Total</p>
            <p className="text-3xl font-black text-blue-700" data-testid="form-total">${(formData.total || 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* SECTION 5: Signatures (Collapsible) */}
      {!editingShipment && (
        <div className="border-2 border-gray-200 rounded-xl overflow-hidden">
          <button type="button" onClick={() => setShowSignatures(!showSignatures)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
            <span className="font-bold text-base text-gray-700">Firmas (Opcional)</span>
            {showSignatures ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
          </button>
          {showSignatures && (
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SignaturePad label="Firma del Remitente" onSave={setFirmaRemitente} savedUrl={firmaRemitente} />
              <SignaturePad label="Firma del Recolector" onSave={setFirmaRecolector} savedUrl={firmaRecolector} />
            </div>
          )}
        </div>
      )}

      {/* SECTION 6: Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" size="lg" onClick={onCancel} className="px-6" data-testid="cancel-shipment-btn">
          Cancelar
        </Button>
        <Button
          data-testid="submit-shipment-btn"
          type="submit" size="lg"
          disabled={submitting || formData.productos.length === 0 || !formData.remitente_id || !formData.destinatario_id}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 px-8 shadow-lg disabled:opacity-50"
        >
          <DollarSign className="mr-2 h-5 w-5" />
          {submitting ? 'Guardando...' : editingShipment ? 'Actualizar Factura' : 'Crear Factura'}
        </Button>
      </div>
    </form>
  );
};

export default ShipmentForm;
